import logger from '../utils/logger.js';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  TextField, 
  Button, 
  Paper, 
  Alert,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Switch
} from '@mui/material';
import { 
  Send, 
  VolumeUp as VolumeUpIcon, 
  Stop as StopIcon,
  Person as PersonIcon,
  LocalHospital as NurseIcon,
  Computer as SoftwareIcon,
  MedicalServices as SuperDoctorIcon,
  ExitToApp as ExitToAppIcon,
  Logout as LogoutIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Language as LanguageIcon,
  Feedback as FeedbackIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { markExplicitLogoutRedirectDefault } from '../utils/authRedirect';
import { SIMULATION_SETUP_PATH } from '../constants/clientRoutes';
import ChatMessageBubble from '../components/ChatMessageBubble';
import PatientV2Badge from '../components/PatientV2Badge';
import PatientProfileAvatar from '../components/PatientProfileAvatar';
import AutoCompactMarker, { findAutoCompactMarkerForMessage } from '../components/AutoCompactMarker';
import VitalSignMonitor from '../components/VitalSignMonitor';
import { normalizeSessionVitals } from '../utils/vitalsDisplay';
import { getLocaleOptions, DEFAULT_LOCALE, LANGUAGE_CODES } from '../config/locales';
import {
  getChatBubbleColors,
  chatRecipientToSenderKey,
  CHAT_SENDER_ICON_SX
} from '../theme';
import axios from 'axios';
import { DEFAULT_SESSION_MODE } from '../utils/sessionMode';

function createChatRequestUuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function getRecipientDisplayName(recipient, { persona, nurse, software, superDoctor }) {
  switch (String(recipient || '').toLowerCase()) {
    case 'nurse':
      return nurse?.name || 'Nurse';
    case 'software':
      return software?.name || 'Software';
    case 'super_doctor':
      return superDoctor?.name || 'Attending Physician';
    case 'patient':
    default:
      return persona?.name || 'Patient';
  }
}

function inferRecipientFromMessagePrefix(messageText) {
  const lower = String(messageText || '').toLowerCase();
  if (
    lower.startsWith('nurse:') ||
    lower.startsWith('nurse ') ||
    lower.startsWith('nurse,')
  ) {
    return 'nurse';
  }
  if (
    lower.startsWith('software:') ||
    lower.startsWith('software ') ||
    lower.startsWith('software,')
  ) {
    return 'software';
  }
  return 'patient';
}

function ChatTypingDots() {
  return (
    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', py: 0.5, px: 0.5 }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'grey.500',
            '@keyframes chatTypingBounce': {
              '0%, 80%, 100%': { transform: 'scale(0.55)', opacity: 0.35 },
              '40%': { transform: 'scale(1)', opacity: 1 },
            },
            animation: 'chatTypingBounce 1.2s ease-in-out infinite',
            animationDelay: `${i * 0.2}s`,
          }}
        />
      ))}
    </Box>
  );
}

const Simulation = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user, isGuest, logout } = useAuth();
  const showTokenUsageTooltip = user?.role === 'admin';
  const showTimingDetails = Boolean(user?.role && user.role !== 'client');

  const handleLogout = () => {
    markExplicitLogoutRedirectDefault();
    logout();
    navigate('/login', { replace: true });
  };
  
  const [messages, setMessages] = useState([]);
  const [compactSummaries, setCompactSummaries] = useState([]);
  const [selectedLocale, setSelectedLocale] = useState(DEFAULT_LOCALE);
  const [localeOptions, setLocaleOptions] = useState([]);
  const [persona, setPersona] = useState(null);
  const [sessionMode, setSessionMode] = useState(DEFAULT_SESSION_MODE);
  const [, setNursePhase] = useState(null);
  const hideDoctorOnlyEvalSections = sessionMode === 'nurse';
  const [nurse, setNurse] = useState(null);
  const [software, setSoftware] = useState(null);
  const [superDoctor, setSuperDoctor] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speakingMessageId, setSpeakingMessageId] = useState(null); // Track which message is being read
  const manualStopRef = useRef(false); // Track if stop was manually triggered
  const [showEndDialog, setShowEndDialog] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [prevIsLoading, setPrevIsLoading] = useState(false);
  /** API recipient (patient | nurse | software | super_doctor) or null while target is unknown. */
  const [respondingPersona, setRespondingPersona] = useState(null);
  const activeChatRequestUuidRef = useRef(null);
  const [error, setError] = useState('');
  
  // Evaluation report state
  const [evaluationReport, setEvaluationReport] = useState(null);

  /** Session vitals object (updated from session load and chat responses). */
  const [sessionVitals, setSessionVitals] = useState(null);

  // Settings: temperature unit, and simulation toggles for router/backend (DICOM, patient select, lab/eval pages).
  // patientSelectMode, labReportPage, and evalReportPage are mutually exclusive — only one may be true at a time.
  const [simulationType, setSimulationType] = useState('patient_exam');
  const classifierAvailable = persona?.has_v2_fields === true;

  const [settings, setSettings] = useState({
    celcius: false,
    dicomRunning: false,
    dicomInstalled: true,
    dicomLicensed: true,
    patientSelectMode: false,
    labReportPage: false,
    evalReportPage: false,
    useAutoCompact: true,
    use_intent3: false,
    use_aws_translate: false,
  });
  const [surgerySettings, setSurgerySettings] = useState({
    currentStep: ''
  });

  // Full list of patient character IDs (for testing: sent as visible patients so patient-select has full roster).
  const [allPatientCharacterIds, setAllPatientCharacterIds] = useState([]);
  // Full list of patient objects from available-personas (for visible-patients card when mode is on).
  const [allPatients, setAllPatients] = useState([]);
  // Visible patients in the patient-select card: full list when mode turns on, then updated by filtered results (local only; reset on reload).
  const [visiblePatientList, setVisiblePatientList] = useState([]);
  // Sort applied to visible list from backend (sort_by, sort_dir); null when unsorted or default.
  const [visiblePatientSort, setVisiblePatientSort] = useState(null);
  
  // Feedback-related state
  const [feedbackInputs, setFeedbackInputs] = useState({});
  const [feedbackLoading, setFeedbackLoading] = useState({});
  const [expandedFeedback, setExpandedFeedback] = useState({});
  
  // Pronunciation expansion state
  const [expandedPronunciation, setExpandedPronunciation] = useState({});
  
  const messagesEndRef = useRef(null);
  const compactSummaryRefreshRef = useRef(null);
  const recognitionRef = useRef(null);
  const synthesisRef = useRef(null);
  const textInputRef = useRef(null);
  const prevPatientSelectModeRef = useRef(settings.patientSelectMode);
  /** Set when a chat response already replaced the visible list, so opening patient select does not overwrite that list with the full roster. */
  const patientListAppliedRef = useRef(false);
  const bringingPatientRef = useRef(false);
  const visiblePatientListScrollRef = useRef(null);
  const clientPrefsLoadedRef = useRef(false);
  const skipClientPrefsPersistRef = useRef(true);
  /** Tracks how the next outgoing client message was composed: typed vs speech-to-text (see request settings.inputSource). */
  const inputSourceForNextSendRef = useRef('webclient_typed');
  const inputHistoryRef = useRef([]);
  const inputHistoryIndexRef = useRef(-1);
  const inputHistoryDraftRef = useRef('');
  const isApplyingInputHistoryRef = useRef(false);

  const getTextInputElement = useCallback(() => {
    if (!textInputRef.current) return null;
    return textInputRef.current.querySelector('textarea') || textInputRef.current.querySelector('input');
  }, []);

  const applyTextInputValue = useCallback((value) => {
    const inputElement = getTextInputElement();
    if (!inputElement) return;
    isApplyingInputHistoryRef.current = true;
    inputElement.value = value;
    inputElement.dispatchEvent(new Event('input', { bubbles: true }));
    isApplyingInputHistoryRef.current = false;
  }, [getTextInputElement]);

  const pushInputHistory = useCallback((text) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const history = inputHistoryRef.current;
    if (history.length > 0 && history[history.length - 1] === trimmed) return;
    const next = [...history, trimmed];
    inputHistoryRef.current = next.length > 100 ? next.slice(-100) : next;
  }, []);

  const navigateInputHistory = useCallback((direction) => {
    const history = inputHistoryRef.current;
    if (history.length === 0) return;

    const inputElement = getTextInputElement();
    if (!inputElement) return;

    let index = inputHistoryIndexRef.current;

    if (direction === 'up') {
      if (index === -1) {
        inputHistoryDraftRef.current = inputElement.value;
        index = history.length - 1;
      } else if (index > 0) {
        index -= 1;
      } else {
        return;
      }
    } else if (index === -1) {
      return;
    } else if (index < history.length - 1) {
      index += 1;
    } else {
      const draft = inputHistoryDraftRef.current;
      inputHistoryIndexRef.current = -1;
      inputHistoryDraftRef.current = '';
      applyTextInputValue(draft);
      inputSourceForNextSendRef.current = 'webclient_typed';
      return;
    }

    inputHistoryIndexRef.current = index;
    applyTextInputValue(history[index]);
    inputSourceForNextSendRef.current = 'webclient_typed';
  }, [applyTextInputValue, getTextInputElement]);

  const resetInputHistoryNavigation = useCallback(() => {
    inputHistoryIndexRef.current = -1;
    inputHistoryDraftRef.current = '';
  }, []);

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          }
        }

        if (finalTranscript) {
          inputSourceForNextSendRef.current = 'webclient_stt';
          const inputElement = getTextInputElement();
          if (inputElement) {
            inputElement.value = `${inputElement.value}${finalTranscript}`;
            inputElement.dispatchEvent(new Event('input', { bubbles: true }));
          }
        }
      };

      recognitionRef.current.onerror = (event) => {
        logger.error('Speech recognition error', event.error);
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }

    // Initialize speech synthesis
    if ('speechSynthesis' in window) {
      synthesisRef.current = window.speechSynthesis;
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (synthesisRef.current) {
        synthesisRef.current.cancel();
      }
    };
  }, [getTextInputElement]);

  // Load locale options
  useEffect(() => {
    const loadLocaleOptions = async () => {
      try {
        const options = await getLocaleOptions();
        setLocaleOptions(options);
      } catch (error) {
        console.error('Failed to load locale options:', error);
        // Fallback to default options
        setLocaleOptions([
          { value: 'en-US', label: '🇺🇸 English (US)', flag: '🇺🇸', name: 'English (US)' }
        ]);
      }
    };
    
    loadLocaleOptions();
  }, []);

  const fetchSessionInfo = useCallback(async () => {
    try {
      const headers = {};
      const token = localStorage.getItem('token');
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.get(`/api/simulation/session/${sessionId}`, { headers });
      setPersona(response.data.persona);
      setSessionMode(response.data.mode || DEFAULT_SESSION_MODE);
      setNursePhase(response.data.nursePhase || null);
      setNurse(response.data.nurse);
      setSoftware(response.data.software);
      setSuperDoctor(response.data.superDoctor);
      setSessionVitals(normalizeSessionVitals(response.data.vitals));
    } catch (error) {
      logger.error('❌ Failed to fetch session info:', error);
      setError('Failed to load simulation session.');
    }
  }, [sessionId]);

  const fetchAvailablePatients = useCallback(async () => {
    try {
      const headers = {};
      const token = localStorage.getItem('token');
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const response = await axios.get('/api/simulation/available-personas', { headers });
      const patients = response.data.patients || [];
      const ids = patients
        .map((p) => (p.character_id != null ? Number(p.character_id) : null))
        .filter((id) => typeof id === 'number' && !Number.isNaN(id));
      setAllPatientCharacterIds(ids);
      setAllPatients(patients);
    } catch (err) {
      logger.error('❌ Failed to fetch available patients for visible list:', err);
    }
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      const headers = {};
      const token = localStorage.getItem('token');
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.get(`/api/simulation/messages/${sessionId}`, { headers });
      const fetchedMessages = response.data.messages || [];
      
      // Process messages to ensure intents are properly formatted (regular, software, surgery, patient select)
      const processedMessages = fetchedMessages.map(message => {
        const allIntents = message.intents || [];
        const isSurgeryMessage = message?.metadata?.simulationType === 'surgery';
        
        const surgeryIntents = allIntents.filter(intent => intent.isSurgeryIntent || (isSurgeryMessage && intent.isSoftwareIntent));
        const regularIntents = allIntents.filter(intent => !intent.isSoftwareIntent && !intent.isSurgeryIntent && !intent.isPatientSelectIntent && !intent.isLabResultsIntent && !intent.isEvalReportIntent);
        const softwareIntents = allIntents.filter(intent => intent.isSoftwareIntent && !intent.isSurgeryIntent && !isSurgeryMessage);
        const patientSelectIntents = allIntents.filter(intent => intent.isPatientSelectIntent);
        const labResultsIntents = allIntents.filter(intent => intent.isLabResultsIntent);
        const evalReportIntents = allIntents.filter(intent => intent.isEvalReportIntent);
        
        const processedMessage = {
          ...message,
          intents: regularIntents,
          softwareIntents: softwareIntents,
          surgeryIntents: surgeryIntents,
          patientSelectIntents: patientSelectIntents,
          labResultsIntents: labResultsIntents,
          evalReportIntents: evalReportIntents,
          dicomMessage: message.metadata?.dicom_message ?? null
        };
        
        // Log messages with intents for debugging
        if (allIntents.length > 0) {
          logger.info('📋 Message with intents loaded:', {
            sender: processedMessage.sender,
            regularIntents,
            softwareIntents,
            surgeryIntents,
            patientSelectIntents,
            labResultsIntents,
            evalReportIntents,
            message: processedMessage.message.substring(0, 50) + '...'
          });
        }
        
        return processedMessage;
      });

      // Attach patient select result (from assistant message metadata) to the previous client message for intent display
      for (let i = 1; i < processedMessages.length; i++) {
        const msg = processedMessages[i];
        const prev = processedMessages[i - 1];
        if (msg.sender === 'software' && msg.metadata?.patientSelect?.filteredPatientIds && prev.sender === 'client') {
          prev.patientSelect = { filteredPatientIds: msg.metadata.patientSelect.filteredPatientIds || [] };
        }
      }
      
      setMessages(processedMessages);
      setCompactSummaries(Array.isArray(response.data.compactSummaries) ? response.data.compactSummaries : []);
      
      // Get evaluation report from response
      if (response.data.evaluationReport) {
        setEvaluationReport(response.data.evaluationReport);
        logger.info('📊 Evaluation Report Loaded:', response.data.evaluationReport);
      }
    } catch (error) {
      logger.error('❌ Failed to fetch messages:', error);
      setError('Failed to load conversation history.');
    }
  }, [sessionId]);

  const fetchCompactSummaries = useCallback(async () => {
    try {
      const headers = {};
      const token = localStorage.getItem('token');
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.get(`/api/simulation/messages/${sessionId}`, { headers });
      setCompactSummaries(Array.isArray(response.data.compactSummaries) ? response.data.compactSummaries : []);
    } catch (error) {
      logger.warn('Failed to refresh auto-compact summaries:', error);
    }
  }, [sessionId]);

  const scheduleCompactSummaryRefresh = useCallback(() => {
    if (compactSummaryRefreshRef.current) {
      clearTimeout(compactSummaryRefreshRef.current);
    }
    // Auto-compact runs async after the response; refresh once compaction likely finished.
    compactSummaryRefreshRef.current = setTimeout(() => {
      compactSummaryRefreshRef.current = null;
      fetchCompactSummaries();
    }, 4000);
  }, [fetchCompactSummaries]);

  useEffect(() => () => {
    if (compactSummaryRefreshRef.current) {
      clearTimeout(compactSummaryRefreshRef.current);
    }
  }, []);

  useEffect(() => {
    if (!sessionId) {
      logger.error('❌ No session ID provided');
      navigate(SIMULATION_SETUP_PATH);
      return;
    }

    fetchSessionInfo();
    fetchMessages();
    fetchAvailablePatients();
  }, [sessionId, fetchSessionInfo, fetchMessages, fetchAvailablePatients, navigate]);

  // Load persisted client chat preferences (temperature unit).
  useEffect(() => {
    let cancelled = false;

    const loadClientPreferences = async () => {
      try {
        const headers = {};
        const token = localStorage.getItem('token');
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const { data } = await axios.get('/api/auth/client-preferences', { headers });
        if (cancelled) return;

        skipClientPrefsPersistRef.current = true;
        setSettings((prev) => ({
          ...prev,
          celcius: data?.celcius === true,
          use_intent3: user?.beta === true && data?.use_intent3 === true,
          use_aws_translate: user?.beta === true && data?.use_aws_translate === true,
        }));
        clientPrefsLoadedRef.current = true;
        window.setTimeout(() => {
          skipClientPrefsPersistRef.current = false;
        }, 0);
      } catch (err) {
        logger.error('❌ Failed to load client preferences:', err);
        clientPrefsLoadedRef.current = true;
        skipClientPrefsPersistRef.current = false;
      }
    };

    loadClientPreferences();
    return () => {
      cancelled = true;
    };
  }, [user?.beta]);

  useEffect(() => {
    if (!clientPrefsLoadedRef.current || skipClientPrefsPersistRef.current) {
      return undefined;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        const headers = {};
        const token = localStorage.getItem('token');
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        await axios.patch(
          '/api/auth/client-preferences',
          {
            celcius: settings.celcius === true,
            ...(user?.beta === true
              ? {
                  use_intent3: settings.use_intent3 === true,
                  use_aws_translate: settings.use_aws_translate === true,
                }
              : {}),
          },
          { headers }
        );
      } catch (err) {
        logger.error('❌ Failed to save client preferences:', err);
      }
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [settings.celcius, settings.use_intent3, settings.use_aws_translate, user?.beta]);

  // In surgery mode, initialize currentStep from the first surgery-knowledge step.
  useEffect(() => {
    if (simulationType !== 'surgery') return;
    if (String(surgerySettings.currentStep).trim() !== '') return;

    let cancelled = false;
    const fetchInitialSurgeryStep = async () => {
      try {
        const headers = {};
        const token = localStorage.getItem('token');
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
        const response = await axios.get('/api/surgery-knowledge', { headers });
        const rows = Array.isArray(response.data) ? response.data : [];
        const firstStep = rows
          .map((row) => Number.parseInt(String(row?.step_number), 10))
          .filter((num) => Number.isInteger(num) && num >= 1)
          .sort((a, b) => a - b)[0];

        if (!cancelled && Number.isInteger(firstStep)) {
          setSurgerySettings((prev) => ({ ...prev, currentStep: String(firstStep) }));
        }
      } catch (error) {
        logger.warn('⚠️ Failed to prepopulate surgery current step:', error);
      }
    };

    fetchInitialSurgeryStep();
    return () => {
      cancelled = true;
    };
  }, [simulationType, surgerySettings.currentStep]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Opening patient select with no list yet shows the full roster. A filter result applied in the same turn wins.
  useEffect(() => {
    const justTurnedOn = settings.patientSelectMode && !prevPatientSelectModeRef.current;
    prevPatientSelectModeRef.current = settings.patientSelectMode;
    if (patientListAppliedRef.current) {
      patientListAppliedRef.current = false;
      return;
    }
    if (settings.patientSelectMode && allPatients.length && (justTurnedOn || visiblePatientList.length === 0)) {
      const sorted = [...allPatients].sort((a, b) => (a.character_id ?? a.id ?? 0) - (b.character_id ?? b.id ?? 0));
      setVisiblePatientList(sorted);
      setVisiblePatientSort(null); // default order by id
    }
  }, [settings.patientSelectMode, allPatients, visiblePatientList.length]);

  // Auto-focus text input after response is received
  useEffect(() => {
    // Only focus when transitioning from loading to not loading
    if (prevIsLoading && !isLoading && textInputRef.current) {
      // Delay to ensure the input is fully enabled and DOM is updated
      setTimeout(() => {
        if (textInputRef.current) {
          // Try to focus the input element directly
          const inputElement = getTextInputElement();
          if (inputElement) {
            inputElement.focus();
          } else {
            textInputRef.current.focus();
          }
        }
      }, 500);
    }
    setPrevIsLoading(isLoading);
  }, [isLoading, prevIsLoading, getTextInputElement]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getVoicesWithRetry = useCallback(
    (callback, retries = 3) => {
      const voices = synthesisRef.current.getVoices();

      if (voices.length > 0) {
        callback(voices);
      } else if (retries > 0) {
        setTimeout(() => {
          getVoicesWithRetry(callback, retries - 1);
        }, 100);
      } else {
        logger.warn('⚠️ No voices available after retries');
        callback([]);
      }
    },
    [] // No dependencies since references are stable (synthesisRef)
  );

  // Stop speech function
  const stopSpeaking = useCallback(() => {
    if (synthesisRef.current) {
      manualStopRef.current = true;
      synthesisRef.current.cancel();
      setIsSpeaking(false);
      setSpeakingMessageId(null);
    }
  }, []);

  const speak = useCallback((text, messageId, language = 'English') => {
    if (synthesisRef.current && text) {
      getVoicesWithRetry((voices) => {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 1; // Normal pace
        utterance.pitch = 1;
        utterance.volume = 1;
        
        // Get language codes for the message language
        const langCodes = LANGUAGE_CODES[language] || LANGUAGE_CODES['English'];
        
        // Filter voices by language
        const matchingVoices = voices.filter(voice => {
          const voiceLang = voice.lang.toLowerCase();
          return langCodes.some(code => voiceLang.startsWith(code.toLowerCase()));
        });
        
        // Sort by priority: Google first, then by region
        matchingVoices.sort((a, b) => {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();
          const aIsGoogle = aName.includes('google');
          const bIsGoogle = bName.includes('google');
          if (aIsGoogle && !bIsGoogle) return -1;
          if (!aIsGoogle && bIsGoogle) return 1;
          const aLang = a.lang.toLowerCase();
          const bLang = b.lang.toLowerCase();
          const aIndex = langCodes.findIndex(code => aLang.startsWith(code.toLowerCase()));
          const bIndex = langCodes.findIndex(code => bLang.startsWith(code.toLowerCase()));
          return aIndex - bIndex;
        });
        
        // Select first matching voice, or fallback to any English voice
        let selectedVoice = matchingVoices.length > 0 ? matchingVoices[0] : null;
        
        if (!selectedVoice) {
          // Fallback to English voices
          selectedVoice = voices.find(voice => voice.lang.startsWith('en'));
          if (!selectedVoice && voices.length > 0) {
            selectedVoice = voices[0];
          }
        }
        
        if (selectedVoice) {
          utterance.voice = selectedVoice;
          utterance.lang = selectedVoice.lang;
          
          // Reset manual stop flag
          manualStopRef.current = false;
          
          utterance.onstart = () => {
            setIsSpeaking(true);
            setSpeakingMessageId(messageId);
          };
          
          utterance.onend = () => {
            setIsSpeaking(false);
            setSpeakingMessageId(null);
          };
          
          utterance.onerror = () => {
            // Don't log error if manually stopped
            if (!manualStopRef.current) {
              logger.error('Speech synthesis error');
            }
            manualStopRef.current = false;
            setIsSpeaking(false);
            setSpeakingMessageId(null);
          };
          
          synthesisRef.current.speak(utterance);
        }
      });
    }
  }, [getVoicesWithRetry]);

  // Toggle speak - start or stop based on current state
  const toggleSpeak = useCallback((text, messageId, language = 'English') => {
    // If this message is currently speaking, stop it
    if (speakingMessageId === messageId) {
      stopSpeaking();
      return;
    }
    
    // If another message is speaking, stop it first
    if (isSpeaking) {
      stopSpeaking();
    }
    
    // Start speaking this message
    speak(text, messageId, language);
  }, [isSpeaking, speakingMessageId, speak, stopSpeaking]);

  const startListening = () => {
    if (recognitionRef.current) {
      setIsListening(true);
      recognitionRef.current.start();
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  const normalizeResponseTimings = (responseData) => {
    const raw = responseData?.timings;
    if (!raw || typeof raw !== 'object') return raw;
    const duration = Number(responseData?.duration);
    const withRequest = {
      ...raw,
      ...(Number.isFinite(duration) && duration > 0 && raw.requestDurationMs == null
        ? { requestDurationMs: Math.round(duration) }
        : {}),
    };
    if (typeof withRequest.messageGeneration === 'number' && !Number.isNaN(withRequest.messageGeneration)) {
      return withRequest;
    }

    // Fallback: derive generation from total duration when backend omitted messageGeneration.
    if (!Number.isFinite(duration) || duration <= 0) return withRequest;
    const translation = Number(raw.translation) || 0;
    const intent = Number(raw.intentIdentification) || 0;
    const target = Number(raw.targetIdentification) || 0;
    const derivedGeneration = Math.max(0, Math.round(duration - translation - intent - target));

    return {
      ...withRequest,
      messageGeneration: derivedGeneration
    };
  };

  const handleSendMessage = async () => {
    const inputElement = getTextInputElement();
    const draftMessage = inputElement?.value || '';
    if (!draftMessage.trim() || isLoading) return;

    const messageText = draftMessage.trim();
    pushInputHistory(messageText);
    resetInputHistoryNavigation();
    if (inputElement) {
      inputElement.value = '';
      inputElement.dispatchEvent(new Event('input', { bubbles: true }));
    }
    
    const isSurgeryChat = simulationType === 'surgery';
    const requestUuid = createChatRequestUuid();
    activeChatRequestUuidRef.current = requestUuid;

    setRespondingPersona(
      isSurgeryChat ? inferRecipientFromMessagePrefix(messageText) : null
    );
    setIsLoading(true);
    setError('');

    const userMessage = {
      id: Date.now(),
      sender: 'client',
      message: messageText,
      timestamp: new Date().toISOString(),
      intents: [],
      surgeryIntents: [],
      language: null // Will be set after API response with actual language
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = localStorage.getItem('token');
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const apiEndpoint = isSurgeryChat ? '/api/surgery-chat/message' : '/api/chat/message';
      const parsedCurrentStep = Number.parseInt(String(surgerySettings.currentStep), 10);
      const inputSource = inputSourceForNextSendRef.current;
      const requestSettings = simulationType === 'surgery'
        ? {
            ...(Number.isInteger(parsedCurrentStep) && parsedCurrentStep >= 0 ? { currentStep: parsedCurrentStep } : {}),
            inputSource
          }
        : {
            ...settings,
            use_intent3: classifierAvailable && settings.use_intent3 === true,
            use_aws_translate: settings.use_aws_translate === true,
            // Send the currently visible card when it has rows; otherwise the room roster.
            // Never omit this list — the backend will not expand it to all patients.
            patientSelectVisiblePatients: (() => {
              const ids = (visiblePatientList || []).map((p) => p.character_id ?? p.id).filter((id) => id != null);
              if (settings.patientSelectMode && ids.length) return ids;
              return allPatientCharacterIds;
            })(),
            inputSource
          };
      const requestPayload = {
        sessionId,
        message: messageText,
        locale: selectedLocale,
        settings: requestSettings,
        ...(isSurgeryChat ? {} : { uuid: requestUuid, requestUuid }),
      };

      if (!isSurgeryChat) {
        axios
          .post(
            '/api/chat/target',
            { sessionId, uuid: requestUuid, requestUuid },
            { headers }
          )
          .then((targetRes) => {
            if (activeChatRequestUuidRef.current !== requestUuid) return;
            const recipient = targetRes.data?.recipient;
            if (recipient) {
              setRespondingPersona(recipient);
            }
          })
          .catch((targetErr) => {
            if (activeChatRequestUuidRef.current !== requestUuid) return;
            logger.info('Chat target poll ended without early recipient', {
              status: targetErr.response?.status,
            });
          });
      }

      const response = await axios.post(apiEndpoint, requestPayload, { headers });
      inputSourceForNextSendRef.current = 'webclient_typed';

      // Log complete server API response to browser console
      logger.info('🩺 Server API Response:', response.data);
      if (response.data.emotions) {
        logger.info('😊 Emotional Analysis:', response.data.emotions);
        logger.info('📊 Emotion Breakdown:', Object.entries(response.data.emotions)
          .map(([emotion, value]) => `${emotion}: ${(value * 100).toFixed(1)}%`)
          .join(', '));
      }
      if (response.data.recipient) {
        logger.info('🎭 Responding Persona:', response.data.recipient);
        setRespondingPersona(response.data.recipient);
      }
      if (response.data.nursePhase != null) {
        const nextPhase = response.data.nursePhase;
        setNursePhase(nextPhase);
        if (sessionMode === 'nurse' && nextPhase === 'report') {
          fetchSessionInfo();
        }
      }
      if (response.data.timings) {
        logger.info('⏱️ Processing Timings:', response.data.timings);
      }
      const normalizedTimings = normalizeResponseTimings(response.data);
      
      // Update evaluation report if provided
      if (response.data.evaluationReport) {
        setEvaluationReport(response.data.evaluationReport);
        logger.info('📊 Evaluation Report Updated:', response.data.evaluationReport);
      }

      if (response.data.vitals) {
        const nextVitals = normalizeSessionVitals(response.data.vitals);
        if (nextVitals) {
          setSessionVitals(nextVitals);
        }
      }

      const assistantReply = typeof response.data.response === 'string' ? response.data.response.trim() : '';
      // Intents, files, and mode changes are already resolved. A missing assistant line must not drop them.
      if (response.data) {
        // Sync patient select / lab results / eval report toggles when user triggers enter/exit via chat
        const patientSelectIntents = response.data.patientSelectIntents || [];
        const enterPatientSelect = patientSelectIntents.some(i => i.action === 'Enter Patient Select');
        const exitPatientSelect = patientSelectIntents.some(i => i.action === 'Exit Patient Select');
        if (enterPatientSelect) {
          setSettings(prev => ({ ...prev, patientSelectMode: true, labReportPage: false, evalReportPage: false }));
        } else if (exitPatientSelect) {
          setSettings(prev => ({ ...prev, patientSelectMode: false }));
        }

        const broughtIn = patientSelectIntents.find(
          (intent) => intent.action === 'Select Patient' && intent.object != null && String(intent.object).trim() !== ''
        );
        if (broughtIn) {
          const characterId = Number(broughtIn.object);
          const rosterMatch = (allPatients || []).find((p) => Number(p.character_id) === characterId);
          const filteredMatch = (response.data.patientSelect?.filteredPatients || []).find(
            (p) => Number(p.character_id) === characterId
          );
          const personaId = rosterMatch?.id ?? filteredMatch?.id ?? null;
          const samePatient = Number(persona?.character_id) === characterId;
          if (personaId && !samePatient && !bringingPatientRef.current) {
            bringingPatientRef.current = true;
            const startHeaders = { 'Content-Type': 'application/json' };
            const startToken = localStorage.getItem('token');
            if (startToken) startHeaders.Authorization = `Bearer ${startToken}`;
            const mode = sessionMode || DEFAULT_SESSION_MODE;
            axios.post('/api/simulation/start', {
              personaId,
              mode,
              ...(mode !== 'nurse' && nurse?.id ? { nursePersonaId: nurse.id } : {}),
              ...(mode !== 'nurse' && software?.id ? { softwarePersonaId: software.id } : {}),
              ...(mode !== 'nurse' && superDoctor?.id ? { superDoctorId: superDoctor.id } : {}),
            }, { headers: startHeaders }).then((startRes) => {
              if (startRes.data?.sessionId) {
                navigate(`/simulation/${startRes.data.sessionId}`);
              } else {
                bringingPatientRef.current = false;
                setError('Failed to start simulation with that patient.');
              }
            }).catch((startErr) => {
              logger.error('Failed to start simulation for selected patient:', startErr);
              bringingPatientRef.current = false;
              setError('Failed to start simulation with that patient.');
            });
          }
        }

        const labResultsIntentsSync = response.data.labResultsIntents || [];
        const enterLabResults = labResultsIntentsSync.some(i => i.action === 'Enter Lab Results');
        const exitLabResults = labResultsIntentsSync.some(i => i.action === 'Exit Lab Results');
        if (enterLabResults) {
          setSettings(prev => ({ ...prev, labReportPage: true, patientSelectMode: false, evalReportPage: false }));
        } else if (exitLabResults) {
          setSettings(prev => ({ ...prev, labReportPage: false }));
        }

        const evalReportIntentsSync = response.data.evalReportIntents || [];
        const enterEvalReport = evalReportIntentsSync.some(i => i.action === 'Enter Eval Report');
        if (enterEvalReport) {
          setSettings(prev => ({ ...prev, evalReportPage: true, patientSelectMode: false, labReportPage: false }));
        }

        // Check what intents we received
        const hasRegularIntents = response.data.intents && response.data.intents.length > 0;
        const hasPatientSelectIntents = response.data.patientSelectIntents && response.data.patientSelectIntents.length > 0;
        const hasLabResultsIntents = response.data.labResultsIntents && response.data.labResultsIntents.length > 0;
        const hasEvalReportIntents = response.data.evalReportIntents && response.data.evalReportIntents.length > 0;
        const hasSoftwareIntents = response.data.softwareIntents && response.data.softwareIntents.length > 0;
        const hasSurgeryIntents = response.data.surgeryIntents && response.data.surgeryIntents.length > 0;
        const fallbackSurgeryIntents = simulationType === 'surgery'
          ? (response.data.intents?.length ? response.data.intents : (response.data.softwareIntents || []))
          : [];
        const hasFallbackSurgeryIntents = fallbackSurgeryIntents.length > 0;
        const hasSoftwareIntent = response.data.softwareIntent && (response.data.softwareIntent.action || response.data.softwareIntent.object);
        const hasLegacyIntent = response.data.intent && (response.data.intent.action || response.data.intent.object);

        // Update doctor's message with language and any found intents
        setMessages(prev => prev.map(msg => {
          if (msg.id === userMessage.id) {
            const updatedMessage = { ...msg };
            if (response.data.messageId) {
              updatedMessage.id = response.data.messageId;
            }
            
            // Add language from response
            if (response.data.language) {
              updatedMessage.language = response.data.language;
            }
            
            // Add regular medical intents
            if (hasRegularIntents) {
              updatedMessage.intents = response.data.intents;
              updatedMessage.intent = response.data.intent;
            }
            
            // Add patient select intents (separate from regular intents)
            if (hasPatientSelectIntents) {
              updatedMessage.patientSelectIntents = response.data.patientSelectIntents;
            }

            // Add lab results intents
            if (hasLabResultsIntents) {
              updatedMessage.labResultsIntents = response.data.labResultsIntents;
            }

            // Add eval report intents
            if (hasEvalReportIntents) {
              updatedMessage.evalReportIntents = response.data.evalReportIntents;
            }
            
            // Add software intents (multiple)
            if (hasSoftwareIntents) {
              updatedMessage.softwareIntents = response.data.softwareIntents;
              updatedMessage.softwareIntent = response.data.softwareIntent;
            }

            // Add surgery intents (separate from regular/software intents)
            if (hasSurgeryIntents) {
              updatedMessage.surgeryIntents = response.data.surgeryIntents;
            } else if (hasFallbackSurgeryIntents) {
              updatedMessage.surgeryIntents = fallbackSurgeryIntents.map((intent) => ({
                ...intent,
                isSurgeryIntent: true,
                isSoftwareIntent: false
              }));
            }

            // Auto-advance the step input when a Show Step surgery intent is received
            const activeSurgeryIntents = updatedMessage.surgeryIntents || [];
            const showStepIntent = activeSurgeryIntents.find(
              (intent) => intent.action === 'Show Step' && intent.object != null && String(intent.object).trim() !== ''
            );
            if (showStepIntent) {
              const newStep = String(showStepIntent.object).trim();
              setSurgerySettings((prev) => ({ ...prev, currentStep: newStep }));
            }
            
            // Add single software intent (backward compatibility)
            if (hasSoftwareIntent && !hasSoftwareIntents) {
              updatedMessage.softwareIntent = response.data.softwareIntent;
            }
            
            // Add legacy intent (backward compatibility)
            if (hasLegacyIntent && !hasRegularIntents) {
              updatedMessage.intent = response.data.intent;
            }

            if (response.data.clientCacheUsage) {
              updatedMessage.cache_usage = response.data.clientCacheUsage;
            }

            // The card is the on-screen list. When the server sends patients, that is the list, in that order.
            if (response.data.patientSelect?.filteredPatients?.length) {
              const byId = new Map((allPatients || []).map((p) => [p.character_id ?? p.id, p]));
              const filtered = response.data.patientSelect.filteredPatients.map((row) => {
                const id = row.character_id ?? row.id;
                const local = byId.get(id);
                return local ? { ...local, ...row, character_id: id } : { ...row, character_id: id };
              });
              const filteredIds = filtered
                .map((p) => p.character_id)
                .filter((id) => id != null);
              updatedMessage.patientSelect = { filteredPatientIds: filteredIds };
              patientListAppliedRef.current = true;
              setVisiblePatientList(filtered);
              setVisiblePatientSort(
                response.data.patientSelect.sort_by != null
                  ? { sort_by: response.data.patientSelect.sort_by, sort_dir: response.data.patientSelect.sort_dir ?? 'asc' }
                  : null
              );
            } else if ((response.data.patientSelectIntents || []).some(i => i.action === 'Reset Filters')) {
              // Reset Filters: backend does not send filtered list; show full list from local allPatients
              const sortBy = response.data.patientSelect?.sort_by ?? 'character_id';
              const sortDir = response.data.patientSelect?.sort_dir ?? 'asc';
              const sorted = [...allPatients].sort((a, b) => {
                const av = a[sortBy] ?? a.character_id ?? a.id ?? 0;
                const bv = b[sortBy] ?? b.character_id ?? b.id ?? 0;
                const cmp = typeof av === 'string' ? String(av).localeCompare(String(bv)) : (av - bv);
                return sortDir === 'desc' ? -cmp : cmp;
              });
              patientListAppliedRef.current = true;
              setVisiblePatientList(sorted);
              setVisiblePatientSort(sortBy !== 'character_id' ? { sort_by: sortBy, sort_dir: sortDir } : null);
            } else if (response.data.patientSelect?.repopulateList && allPatients.length) {
              // repopulateList true but no filtered list (e.g. entering patient select): show full list.
              // Empty filter matches return repopulateList false so the visible list is left alone.
              const sortBy = response.data.patientSelect?.sort_by ?? 'character_id';
              const sortDir = response.data.patientSelect?.sort_dir ?? 'asc';
              const sorted = [...allPatients].sort((a, b) => {
                const av = a[sortBy] ?? a.character_id ?? a.id ?? 0;
                const bv = b[sortBy] ?? b.character_id ?? b.id ?? 0;
                const cmp = typeof av === 'string' ? String(av).localeCompare(String(bv)) : (av - bv);
                return sortDir === 'desc' ? -cmp : cmp;
              });
              patientListAppliedRef.current = true;
              setVisiblePatientList(sorted);
              setVisiblePatientSort(sortBy !== 'character_id' ? { sort_by: sortBy, sort_dir: sortDir } : null);
            }

            return updatedMessage;
          }
          return msg;
        }));
        
        if (assistantReply) {
          const aiMessage = {
            id: response.data.id || Date.now() + 1,
            sender: response.data.recipient || 'patient',
            message: response.data.response,
            timestamp: new Date().toISOString(),
            timings: normalizedTimings,
            language: response.data.language,
            pronunciation: response.data.pronunciation,
            cache_usage: response.data.cacheUsage || null,
            metadata: response.data.fallback ? { fallback: response.data.fallback } : null,
          };

          setMessages(prev => [...prev, aiMessage]);
          scheduleCompactSummaryRefresh();
        }
      }
    } catch (error) {
      logger.error('❌ Failed to send message:', error);
      setError('Failed to send message. Please try again.');
      setMessages(prev => prev.filter(msg => msg.id !== userMessage.id));
    } finally {
      activeChatRequestUuidRef.current = null;
      setIsLoading(false);
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      navigateInputHistory('up');
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      navigateInputHistory('down');
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleVoiceInput = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const handleEndSimulation = () => {
    setShowEndDialog(true);
  };

  const confirmEndSimulation = async () => {
    try {
      const headers = {};
      const token = localStorage.getItem('token');
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      if (isGuest) {
        setShowEndDialog(false);
        navigate(SIMULATION_SETUP_PATH);
        return;
      }

      const response = await axios.post(`/api/simulation/end/${sessionId}`, {}, { headers });
      
      // If session was deleted (had 1 or less messages), redirect to main page
      if (response.data.deleted) {
        setShowEndDialog(false);
        navigate(SIMULATION_SETUP_PATH);
        return;
      }
      
      setEvaluation(response.data.evaluation);
      setShowEndDialog(false);
    } catch (error) {
      logger.error('❌ Failed to end simulation:', error);
      setShowEndDialog(false);
      navigate(SIMULATION_SETUP_PATH);
    }
  };

  // Handle feedback submission
  const handleFeedbackSubmit = useCallback(async (messageId) => {
    const feedbackText = feedbackInputs[messageId];
    if (!feedbackText || !feedbackText.trim()) {
      return;
    }

    setFeedbackLoading(prev => ({ ...prev, [messageId]: true }));

    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = localStorage.getItem('token');
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      await axios.post('/api/chat/feedback', {
        sessionId,
        comments: feedbackText.trim()
      }, { headers });

      // Clear the feedback input after successful submission
      setFeedbackInputs(prev => ({ ...prev, [messageId]: '' }));
      
      logger.info('✅ Feedback submitted successfully for session:', sessionId);
    } catch (error) {
      logger.error('❌ Failed to submit feedback:', error);
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setFeedbackLoading(prev => ({ ...prev, [messageId]: false }));
    }
  }, [feedbackInputs, sessionId]);

  // Handle feedback input change
  const handleFeedbackInputChange = useCallback((messageId, value) => {
    setFeedbackInputs(prev => ({ ...prev, [messageId]: value }));
  }, []);

  // Handle feedback expansion toggle
  const toggleFeedbackExpansion = useCallback((messageId) => {
    setExpandedFeedback(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  }, []);

  // Handle pronunciation expansion toggle
  const togglePronunciation = useCallback((messageId) => {
    setExpandedPronunciation(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  }, []);

  // Function to render a single message
  const renderMessage = useCallback((message) => {
    const isClient = message.sender === 'client';
    const isPatient = message.sender === 'patient';
    const isNurse = message.sender === 'nurse';
    const isSoftware = message.sender === 'software';
    const isSystem = message.sender === 'system';
    const isSuperDoctor = message.sender === 'super_doctor';

    const senderKey = isClient
      ? 'client'
      : isPatient
        ? 'patient'
        : isNurse
          ? 'nurse'
          : isSoftware || isSystem
            ? 'software'
            : isSuperDoctor
              ? 'super_doctor'
              : 'default';
    const { bg: bgColor, text: textColor } = getChatBubbleColors(senderKey);

    let senderName = message.sender;
    let senderIcon = <PersonIcon sx={CHAT_SENDER_ICON_SX} />;
    if (isClient) {
      senderName = user?.username || 'You';
      senderIcon = <PersonIcon sx={CHAT_SENDER_ICON_SX} />;
    } else if (isPatient && persona) {
      senderName = persona.name;
      senderIcon = <PersonIcon sx={CHAT_SENDER_ICON_SX} />;
    } else if (isNurse && nurse) {
      senderName = nurse.name;
      senderIcon = <NurseIcon sx={CHAT_SENDER_ICON_SX} />;
    } else if (isSystem) {
      senderName = 'System';
      senderIcon = <SoftwareIcon sx={CHAT_SENDER_ICON_SX} />;
    } else if (isSoftware) {
      senderName = software?.name || getRecipientDisplayName('software', { persona, nurse, software, superDoctor });
      senderIcon = <SoftwareIcon sx={CHAT_SENDER_ICON_SX} />;
    } else if (isSuperDoctor) {
      senderName = superDoctor?.name || getRecipientDisplayName('super_doctor', { persona, nurse, software, superDoctor });
      senderIcon = <SuperDoctorIcon sx={CHAT_SENDER_ICON_SX} />;
    }

    const hasPronunciation = !isClient && message.pronunciation && message.pronunciation !== message.message;
    const isExpanded = expandedPronunciation[message.id];

    const actions = (
      <>
        {!isClient && (
          <Tooltip title={speakingMessageId === message.id ? 'Stop' : 'Read aloud'} arrow placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                const textToSpeak = message.pronunciation ||
                  (typeof message.message === 'string' ? message.message : JSON.stringify(message.message));
                toggleSpeak(textToSpeak, message.id, message.language || 'English');
              }}
              sx={{
                color: speakingMessageId === message.id ? 'error.main' : 'text.secondary',
                '&:hover': {
                  color: speakingMessageId === message.id ? 'error.dark' : 'primary.main',
                  backgroundColor: 'action.hover'
                }
              }}
            >
              {speakingMessageId === message.id ? <StopIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={expandedFeedback[message.id] ? 'Hide feedback' : 'Add feedback'} arrow placement="top">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              toggleFeedbackExpansion(message.id);
            }}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main',
                backgroundColor: 'action.hover'
              }
            }}
          >
            <FeedbackIcon fontSize="small" />
            {expandedFeedback[message.id] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </>
    );

    const footer = expandedFeedback[message.id] ? (
      <Box sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'stretch' }}>
          <TextField
            size="small"
            multiline
            maxRows={2}
            placeholder="Provide feedback on this message..."
            value={feedbackInputs[message.id] || ''}
            onChange={(e) => handleFeedbackInputChange(message.id, e.target.value)}
            onClick={(e) => e.stopPropagation()}
            disabled={feedbackLoading[message.id]}
            sx={{ flex: 1 }}
            variant="outlined"
          />
          <Tooltip title={feedbackLoading[message.id] ? 'Saving...' : 'Send feedback'} arrow placement="top">
            <span style={{ display: 'inline-flex' }}>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleFeedbackSubmit(message.id);
                }}
                disabled={!feedbackInputs[message.id]?.trim() || feedbackLoading[message.id]}
                sx={{
                  color: 'primary.main',
                  minHeight: '40px',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText'
                  },
                  '&:disabled': {
                    color: 'action.disabled'
                  }
                }}
              >
                <Send fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    ) : null;

    return (
      <ChatMessageBubble
        key={message.id}
        message={message}
        alignRight={isClient}
        backgroundColor={bgColor}
        textColor={textColor}
        senderName={senderName}
        senderIcon={senderIcon}
        onClick={() => hasPronunciation && togglePronunciation(message.id)}
        clickable={hasPronunciation}
        showTimings={!isClient}
        showTimingDetails={showTimingDetails}
        showLanguage
        showTokenUsageTooltip={showTokenUsageTooltip}
        showIntents={isClient}
        intentProps={{
          intents: message.intents,
          softwareIntents: message.softwareIntents,
          surgeryIntents: message.surgeryIntents,
          patientSelectIntents: message.patientSelectIntents,
          labResultsIntents: message.labResultsIntents,
          evalReportIntents: message.evalReportIntents,
          legacyIntent: message.intent,
          softwareIntent: message.softwareIntent,
          patientSelect: message.patientSelect,
          dicomMessage: message.dicomMessage ?? message.metadata?.dicom_message ?? null
        }}
        actions={actions}
        footer={footer}
      >
        {hasPronunciation && (
          <Box
            sx={{
              overflow: 'hidden',
              maxHeight: isExpanded ? '500px' : '0px',
              opacity: isExpanded ? 1 : 0,
              transition: 'max-height 0.3s ease-in-out, opacity 0.2s ease-in-out',
              mt: isExpanded ? 1.5 : 0
            }}
          >
            <Box
              sx={{
                p: 1.5,
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderRadius: 1,
                borderLeft: '3px solid',
                borderColor: textColor
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                Pronunciation:
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                {message.pronunciation}
              </Typography>
            </Box>
          </Box>
        )}
        {hasPronunciation && !isExpanded && (
          <Typography
            variant="caption"
            sx={{ color: 'text.disabled', fontStyle: 'italic', display: 'block', mt: 0.5, fontSize: '0.7rem' }}
          >
            Click to show pronunciation
          </Typography>
        )}
      </ChatMessageBubble>
    );
  }, [
    expandedFeedback,
    expandedPronunciation,
    feedbackInputs,
    feedbackLoading,
    nurse,
    persona,
    software,
    speakingMessageId,
    superDoctor,
    toggleFeedbackExpansion,
    togglePronunciation,
    toggleSpeak,
    user?.username,
    showTokenUsageTooltip,
    showTimingDetails,
    handleFeedbackInputChange,
    handleFeedbackSubmit
  ]);

  const renderedMessages = useMemo(
    () => messages.map((msg, idx) => {
      const marker = findAutoCompactMarkerForMessage(compactSummaries, msg, messages[idx + 1]);
      return (
        <React.Fragment key={msg.id}>
          {renderMessage(msg)}
          {marker && <AutoCompactMarker summary={marker} />}
        </React.Fragment>
      );
    }),
    [messages, compactSummaries, renderMessage]
  );

  if (!sessionId) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          No session ID provided. Please start a simulation by selecting a patient.
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate(SIMULATION_SETUP_PATH)}
          sx={{ mt: 2 }}
        >
          Select Patient
        </Button>
      </Container>
    );
  }

  return (
    <Container
      maxWidth={false}
      sx={{
        maxWidth: 1728,
        mx: 'auto',
        py: 2,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Paper elevation={1} sx={{ p: 1.5, mb: 1.5, flexShrink: 0 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            {persona ? (
              <PatientProfileAvatar persona={persona} size={40} />
            ) : (
              <PersonIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            )}
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {persona ? `Simulation with ${persona.name}` : 'Loading Simulation...'}
                {nurse && ` and Nurse ${nurse.name}`}
                {software && ` and Software ${software.name}`}
                {superDoctor && ` and ${superDoctor.name}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Session #{sessionId}
                {persona && (
                  <> • Patient: {persona.age} years old • {persona.gender} • {persona.chief_complaint}</>
                )}
              </Typography>
            </Box>
          </Box>
          
          <Box display="flex" gap={1}>
            {user && !isGuest && (
              <Button
                variant="outlined"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                size="small"
              >
                Sign Out
              </Button>
            )}
            <Button 
              variant="outlined" 
              color="error"
              startIcon={<ExitToAppIcon />}
              onClick={handleEndSimulation}
              size="small"
            >
              End Simulation
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', gap: 2 }}>
        {/* Chat Area */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* Messages */}
          <Box 
            sx={{ 
              flex: 1, 
              overflowY: 'auto', 
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.paper',
              maxHeight: 'calc(100vh - 200px)', // Ensure it doesn't exceed viewport
              minHeight: '400px' // Minimum height for usability
            }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {messages.length === 0 && !isLoading && (
              <Box sx={{ 
                textAlign: 'center', 
                py: 4,
                color: 'text.secondary'
              }}>
                <PersonIcon sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Start your conversation with {persona?.name || 'the patient'}
                  {nurse && ` and Nurse ${nurse.name}`}
                  {software && ` and Software ${software.name}`}
                  {superDoctor && ` and ${superDoctor.name}`}
                </Typography>
                <Typography variant="body2">
                  Begin by introducing yourself and asking about their chief complaint.
                  {nurse && (
                    <>
                      <br />
                      <strong>Tip:</strong> Address the nurse with "Nurse", "{nurse.name}", or by their first/last name.
                    </>
                  )}
                  {software && (
                    <>
                      <br />
                      <strong>Tip:</strong> Control software with "Software", "System", "Computer", or "{software.name}".
                    </>
                  )}
                  {superDoctor && (
                    <>
                      <br />
                      <strong>Tip:</strong> Consult the Attending Physician with "Doctor", "Professor", "{superDoctor.name}", or by their first/last name.
                    </>
                  )}
                </Typography>
              </Box>
            )}

            {renderedMessages}

            {isLoading && (() => {
              const typingColors = respondingPersona
                ? getChatBubbleColors(chatRecipientToSenderKey(respondingPersona))
                : { bg: '#F5F5F5', text: '#424242' };
              const typingLabel = respondingPersona
                ? `${getRecipientDisplayName(respondingPersona, { persona, nurse, software, superDoctor })} is responding...`
                : null;
              return (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      ml: 0,
                      mr: 4,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: typingColors.bg,
                      minWidth: respondingPersona ? undefined : 72,
                    }}
                  >
                    {typingLabel ? (
                      <Typography variant="body2" sx={{ color: typingColors.text, fontWeight: 500 }}>
                        {typingLabel}
                      </Typography>
                    ) : (
                      <ChatTypingDots />
                    )}
                  </Paper>
                </Box>
              );
            })()}
            
            <div ref={messagesEndRef} />
          </Box>

          {/* Input Area */}
          <Paper 
            elevation={1}
            sx={{ 
              p: 2, 
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper'
            }}
          >
            <Box display="flex" alignItems="flex-end" gap={1}>
              <TextField
                ref={textInputRef}
                fullWidth
                multiline
                maxRows={3}
                onChange={() => {
                  if (isApplyingInputHistoryRef.current) return;
                  inputSourceForNextSendRef.current = 'webclient_typed';
                  if (inputHistoryIndexRef.current !== -1) {
                    resetInputHistoryNavigation();
                  }
                }}
                onKeyDown={handleInputKeyDown}
                placeholder="Type message..."
                variant="outlined"
                disabled={isLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
              
              <Tooltip title={isListening ? 'Stop voice input' : 'Voice input'} arrow placement="top">
                <span style={{ display: 'inline-flex' }}>
                  <IconButton
                    color={isListening ? 'error' : 'primary'}
                    onClick={handleVoiceInput}
                    disabled={isLoading}
                    sx={{
                      p: 1.5,
                      bgcolor: isListening ? 'error.light' : 'transparent',
                      '&:hover': {
                        bgcolor: isListening ? 'error.main' : 'action.hover'
                      }
                    }}
                  >
                    {isListening ? <MicOffIcon /> : <MicIcon />}
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Send message" arrow placement="top">
                <span style={{ display: 'inline-flex' }}>
                  <IconButton
                    color="primary"
                    onClick={handleSendMessage}
                    disabled={isLoading}
                    sx={{
                      p: 1.5,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': {
                        bgcolor: 'primary.dark'
                      },
                      '&:disabled': {
                        bgcolor: 'action.disabledBackground',
                        color: 'action.disabled'
                      }
                    }}
                  >
                    <Send />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            
            {isListening && (
              <Box sx={{ mt: 1 }}>
                <Chip
                  label="🎤 Listening..."
                  color="error"
                  variant="outlined"
                  size="small"
                />
              </Box>
            )}
          </Paper>
        </Box>

        {/* Right Sidebar */}
        <Box sx={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Simulation Type Card */}
          <Paper elevation={2} sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <AssessmentIcon color="primary" />
              <Typography variant="h6" color="primary.main" fontWeight={600}>
                Simulation Type
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select the type of simulation to run
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel id="simulation-type-label">Type</InputLabel>
              <Select
                labelId="simulation-type-label"
                value={simulationType}
                label="Type"
                onChange={(e) => setSimulationType(e.target.value)}
                disabled={isLoading}
              >
                <MenuItem value="patient_exam">Patient Exam</MenuItem>
                <MenuItem value="surgery">Surgery</MenuItem>
              </Select>
            </FormControl>
          </Paper>

          {/* Language Selection Card */}
          <Paper elevation={2} sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <LanguageIcon color="primary" />
              <Typography variant="h6" color="primary.main" fontWeight={600}>
                Language Settings
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select the language for your messages and responses
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel id="locale-select-label">Language</InputLabel>
              <Select
                labelId="locale-select-label"
                value={selectedLocale}
                label="Language"
                onChange={(e) => setSelectedLocale(e.target.value)}
                disabled={isLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              >
                {localeOptions.map((locale) => (
                  <MenuItem key={locale.value} value={locale.value}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <span>{locale.flag}</span>
                      <span>{locale.name}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>

          {simulationType === 'patient_exam' && (
            <VitalSignMonitor vitals={sessionVitals} celcius={settings.celcius} />
          )}

          {/* Evaluation Report Panel */}
          {evaluationReport && (
            <Paper elevation={2} sx={{ p: 2, height: 'fit-content', maxHeight: '500px', overflow: 'auto' }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <AssessmentIcon color="primary" />
                <Typography variant="h6" color="primary.main" fontWeight={600}>
                  Evaluation Report
                </Typography>
              </Box>

              {/* Medical History Section */}
              {evaluationReport.medical_history && (
                <Tooltip 
                  title={
                    <Box sx={{ p: 1.5, minWidth: 250, maxWidth: 400 }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: 'white', mb: 1.5 }}>
                        Completed Items:
                      </Typography>
                      {evaluationReport.medical_history.executed_objects?.length > 0 ? (
                        evaluationReport.medical_history.executed_objects.map((obj, idx) => (
                          <Typography key={idx} variant="body2" display="block" sx={{ color: 'white', mb: 0.75, lineHeight: 1.6 }}>
                            • {obj.object}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2" display="block" sx={{ color: 'white' }}>
                          No items completed yet
                        </Typography>
                      )}
                    </Box>
                  }
                  placement="left"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: 'rgba(0, 0, 0, 0.9)',
                        '& .MuiTooltip-arrow': {
                          color: 'rgba(0, 0, 0, 0.9)',
                        },
                      },
                    },
                  }}
                >
                  <Box sx={{ mb: 2, cursor: 'pointer' }}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" fontWeight={500}>
                        Medical History
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {evaluationReport.medical_history.total - evaluationReport.medical_history.remaining}/{evaluationReport.medical_history.total}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={evaluationReport.medical_history.total > 0 
                        ? ((evaluationReport.medical_history.total - evaluationReport.medical_history.remaining) / evaluationReport.medical_history.total) * 100 
                        : 0
                      }
                      color="primary"
                      sx={{ height: 8, borderRadius: 1 }}
                    />
                  </Box>
                </Tooltip>
              )}

              {/* Exam Section */}
              {evaluationReport.exam && (
                <Tooltip 
                  title={
                    <Box sx={{ p: 1.5, minWidth: 250, maxWidth: 400 }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: 'white', mb: 1.5 }}>
                        Completed Items:
                      </Typography>
                      {evaluationReport.exam.executed_objects?.length > 0 ? (
                        evaluationReport.exam.executed_objects.map((obj, idx) => (
                          <Typography key={idx} variant="body2" display="block" sx={{ color: 'white', mb: 0.75, lineHeight: 1.6 }}>
                            • {obj.object}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2" display="block" sx={{ color: 'white' }}>
                          No items completed yet
                        </Typography>
                      )}
                    </Box>
                  }
                  placement="left"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: 'rgba(0, 0, 0, 0.9)',
                        '& .MuiTooltip-arrow': {
                          color: 'rgba(0, 0, 0, 0.9)',
                        },
                      },
                    },
                  }}
                >
                  <Box sx={{ mb: 2, cursor: 'pointer' }}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" fontWeight={500}>
                        Physical Exam
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {evaluationReport.exam.total - evaluationReport.exam.remaining}/{evaluationReport.exam.total}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={evaluationReport.exam.total > 0 
                        ? ((evaluationReport.exam.total - evaluationReport.exam.remaining) / evaluationReport.exam.total) * 100 
                        : 0
                      }
                      color="success"
                      sx={{ height: 8, borderRadius: 1 }}
                    />
                  </Box>
                </Tooltip>
              )}

              {/* Investigate Section */}
              {evaluationReport.investigate && (
                <Tooltip 
                  title={
                    <Box sx={{ p: 1.5, minWidth: 250, maxWidth: 400 }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: 'white', mb: 1.5 }}>
                        Completed Items:
                      </Typography>
                      {evaluationReport.investigate.executed_objects?.length > 0 ? (
                        evaluationReport.investigate.executed_objects.map((obj, idx) => (
                          <Typography key={idx} variant="body2" display="block" sx={{ color: 'white', mb: 0.75, lineHeight: 1.6 }}>
                            • {obj.object}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2" display="block" sx={{ color: 'white' }}>
                          No items completed yet
                        </Typography>
                      )}
                    </Box>
                  }
                  placement="left"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: 'rgba(0, 0, 0, 0.9)',
                        '& .MuiTooltip-arrow': {
                          color: 'rgba(0, 0, 0, 0.9)',
                        },
                      },
                    },
                  }}
                >
                  <Box sx={{ mb: 2, cursor: 'pointer' }}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" fontWeight={500}>
                        Investigations
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {evaluationReport.investigate.total - evaluationReport.investigate.remaining}/{evaluationReport.investigate.total}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={evaluationReport.investigate.total > 0 
                        ? ((evaluationReport.investigate.total - evaluationReport.investigate.remaining) / evaluationReport.investigate.total) * 100 
                        : 0
                      }
                      color="warning"
                      sx={{ height: 8, borderRadius: 1 }}
                    />
                  </Box>
                </Tooltip>
              )}

              {/* Intervene Section */}
              {evaluationReport.intervene && (
                <Tooltip 
                  title={
                    <Box sx={{ p: 1.5, minWidth: 250, maxWidth: 400 }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: 'white', mb: 1.5 }}>
                        Completed Items:
                      </Typography>
                      {evaluationReport.intervene.executed_objects?.length > 0 ? (
                        evaluationReport.intervene.executed_objects.map((obj, idx) => (
                          <Typography key={idx} variant="body2" display="block" sx={{ color: 'white', mb: 0.75, lineHeight: 1.6 }}>
                            • {obj.object}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2" display="block" sx={{ color: 'white' }}>
                          No items completed yet
                        </Typography>
                      )}
                    </Box>
                  }
                  placement="left"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: 'rgba(0, 0, 0, 0.9)',
                        '& .MuiTooltip-arrow': {
                          color: 'rgba(0, 0, 0, 0.9)',
                        },
                      },
                    },
                  }}
                >
                  <Box sx={{ mb: 2, cursor: 'pointer' }}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" fontWeight={500}>
                        Interventions
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {evaluationReport.intervene.total - evaluationReport.intervene.remaining}/{evaluationReport.intervene.total}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={evaluationReport.intervene.total > 0 
                        ? ((evaluationReport.intervene.total - evaluationReport.intervene.remaining) / evaluationReport.intervene.total) * 100 
                        : 0
                      }
                      color="error"
                      sx={{ height: 8, borderRadius: 1 }}
                    />
                  </Box>
                </Tooltip>
              )}

              {/* Consultation Section */}
              {!hideDoctorOnlyEvalSections && evaluationReport.consultation && (
                <Tooltip 
                  title={
                    <Box sx={{ p: 1.5, minWidth: 250, maxWidth: 400 }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: 'white', mb: 1.5 }}>
                        Completed Items:
                      </Typography>
                      {evaluationReport.consultation.executed_objects?.length > 0 ? (
                        evaluationReport.consultation.executed_objects.map((obj, idx) => (
                          <Typography key={idx} variant="body2" display="block" sx={{ color: 'white', mb: 0.75, lineHeight: 1.6 }}>
                            • {obj.object}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2" display="block" sx={{ color: 'white' }}>
                          No items completed yet
                        </Typography>
                      )}
                    </Box>
                  }
                  placement="left"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: 'rgba(0, 0, 0, 0.9)',
                        '& .MuiTooltip-arrow': {
                          color: 'rgba(0, 0, 0, 0.9)',
                        },
                      },
                    },
                  }}
                >
                  <Box sx={{ mb: 2, cursor: 'pointer' }}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" fontWeight={500}>
                        Consultation
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {evaluationReport.consultation.total - evaluationReport.consultation.remaining}/{evaluationReport.consultation.total}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={evaluationReport.consultation.total > 0 
                        ? ((evaluationReport.consultation.total - evaluationReport.consultation.remaining) / evaluationReport.consultation.total) * 100 
                        : 0
                      }
                      color="secondary"
                      sx={{ height: 8, borderRadius: 1 }}
                    />
                  </Box>
                </Tooltip>
              )}

              {/* Diagnosis Section */}
              {!hideDoctorOnlyEvalSections && evaluationReport.diagnosis && (
                <Tooltip 
                  title={
                    <Box sx={{ p: 1.5, minWidth: 250, maxWidth: 400 }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: 'white', mb: 1.5 }}>
                        Completed Items:
                      </Typography>
                      {evaluationReport.diagnosis.latest && evaluationReport.diagnosis.latest.object && (
                        <Typography variant="body2" display="block" sx={{ color: 'white', mb: 1, lineHeight: 1.6, fontStyle: 'italic' }}>
                          Latest: {evaluationReport.diagnosis.latest.object}
                          {evaluationReport.diagnosis.latest.evaluation && ` (${evaluationReport.diagnosis.latest.evaluation})`}
                        </Typography>
                      )}
                      {evaluationReport.diagnosis.executed_objects?.length > 0 ? (
                        evaluationReport.diagnosis.executed_objects.map((obj, idx) => (
                          <Typography key={idx} variant="body2" display="block" sx={{ color: 'white', mb: 0.75, lineHeight: 1.6 }}>
                            • {obj.object}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2" display="block" sx={{ color: 'white' }}>
                          No items completed yet
                        </Typography>
                      )}
                    </Box>
                  }
                  placement="left"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: 'rgba(0, 0, 0, 0.9)',
                        '& .MuiTooltip-arrow': {
                          color: 'rgba(0, 0, 0, 0.9)',
                        },
                      },
                    },
                  }}
                >
                  <Box sx={{ mb: 2, cursor: 'pointer' }}>
                    <Typography variant="body2" fontWeight={500} mb={0.5}>
                      Diagnosis
                    </Typography>
                    {evaluationReport.diagnosis.latest && evaluationReport.diagnosis.latest.object ? (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Latest: {evaluationReport.diagnosis.latest.object}
                        </Typography>
                        {evaluationReport.diagnosis.latest.evaluation && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block' }}>
                            ({evaluationReport.diagnosis.latest.evaluation})
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No diagnosis executed yet
                      </Typography>
                    )}
                  </Box>
                </Tooltip>
              )}

              {/* Handoff Section */}
              {!hideDoctorOnlyEvalSections && evaluationReport.handoff && (
                <Tooltip 
                  title={
                    <Box sx={{ p: 1.5, minWidth: 250, maxWidth: 400 }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: 'white', mb: 1.5 }}>
                        Completed Items:
                      </Typography>
                      {evaluationReport.handoff.latest && evaluationReport.handoff.latest.object && (
                        <Typography variant="body2" display="block" sx={{ color: 'white', mb: 1, lineHeight: 1.6, fontStyle: 'italic' }}>
                          Latest: {evaluationReport.handoff.latest.object}
                          {evaluationReport.handoff.latest.evaluation && ` (${evaluationReport.handoff.latest.evaluation})`}
                        </Typography>
                      )}
                      {evaluationReport.handoff.executed_objects?.length > 0 ? (
                        evaluationReport.handoff.executed_objects.map((obj, idx) => (
                          <Typography key={idx} variant="body2" display="block" sx={{ color: 'white', mb: 0.75, lineHeight: 1.6 }}>
                            • {obj.object}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2" display="block" sx={{ color: 'white' }}>
                          No items completed yet
                        </Typography>
                      )}
                    </Box>
                  }
                  placement="left"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: 'rgba(0, 0, 0, 0.9)',
                        '& .MuiTooltip-arrow': {
                          color: 'rgba(0, 0, 0, 0.9)',
                        },
                      },
                    },
                  }}
                >
                  <Box sx={{ mb: 2, cursor: 'pointer' }}>
                    <Typography variant="body2" fontWeight={500} mb={0.5}>
                      Handoff
                    </Typography>
                    {evaluationReport.handoff.latest && evaluationReport.handoff.latest.object ? (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Latest: {evaluationReport.handoff.latest.object}
                        </Typography>
                        {evaluationReport.handoff.latest.evaluation && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block' }}>
                            ({evaluationReport.handoff.latest.evaluation})
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No handoff executed yet
                      </Typography>
                    )}
                  </Box>
                </Tooltip>
              )}
            </Paper>
          )}

        </Box>

        {/* Settings panel - temperature, DICOM, patient select (simulate state for backend) */}
        <Box sx={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Paper elevation={2} sx={{ p: 2, height: 'fit-content' }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Typography variant="h6" color="primary.main" fontWeight={600}>
                Settings
              </Typography>
            </Box>
            {simulationType === 'surgery' ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  These settings are sent to surgery chat and can influence surgery-control behavior.
                </Typography>
                <TextField
                  label="Current Step"
                  type="number"
                  size="small"
                  value={surgerySettings.currentStep}
                  onChange={(e) => setSurgerySettings((prev) => ({ ...prev, currentStep: e.target.value }))}
                  inputProps={{ min: 0, step: 1 }}
                  helperText="Optional. Used for previous/next step navigation."
                />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Temperature unit
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color={!settings.celcius ? 'primary.main' : 'text.secondary'} fontWeight={!settings.celcius ? 600 : 400}>
                      °F
                    </Typography>
                    <Switch
                      size="small"
                      checked={settings.celcius}
                      onChange={() => setSettings(prev => ({ ...prev, celcius: !prev.celcius }))}
                      color="primary"
                    />
                    <Typography variant="caption" color={settings.celcius ? 'primary.main' : 'text.secondary'} fontWeight={settings.celcius ? 600 : 400}>
                      °C
                    </Typography>
                  </Box>
                </Box>
                {user?.beta === true && (
                  <>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Intent 3.0 (TypeSafe)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {classifierAvailable
                          ? 'Beta: TypeSafe picks Patient, Doctor, or System, medical history, and ambiguous intents'
                          : 'Not available — this patient has no v2 fields'}
                      </Typography>
                    </Box>
                    <Tooltip
                      title="Intent 3.0 needs a patient with v2 fields"
                      placement="left"
                      arrow
                      disableHoverListener={classifierAvailable}
                    >
                      <span>
                        <Switch
                          size="small"
                          checked={classifierAvailable && settings.use_intent3 === true}
                          disabled={!classifierAvailable}
                          onChange={() => setSettings((prev) => ({
                            ...prev,
                            use_intent3: !prev.use_intent3,
                          }))}
                          color="primary"
                        />
                      </span>
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        AWS Translate
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Beta: Amazon Translate for this chat instead of the language model
                      </Typography>
                    </Box>
                    <Switch
                      size="small"
                      checked={settings.use_aws_translate === true}
                      onChange={() => setSettings((prev) => ({
                        ...prev,
                        use_aws_translate: !prev.use_aws_translate,
                      }))}
                      color="primary"
                    />
                  </Box>
                  </>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    DICOM viewer installed
                  </Typography>
                  <Switch
                    size="small"
                    checked={settings.dicomInstalled}
                    onChange={() => setSettings(prev => ({ ...prev, dicomInstalled: !prev.dicomInstalled }))}
                    color="primary"
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    DICOM viewer licensed
                  </Typography>
                  <Switch
                    size="small"
                    checked={settings.dicomLicensed}
                    onChange={() => setSettings(prev => ({ ...prev, dicomLicensed: !prev.dicomLicensed }))}
                    color="primary"
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    DICOM viewer open
                  </Typography>
                  <Switch
                    size="small"
                    checked={settings.dicomRunning}
                    onChange={() => setSettings(prev => ({ ...prev, dicomRunning: !prev.dicomRunning }))}
                    color="primary"
                  />
                </Box>
                <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" color="primary.main" fontWeight={600}>
                    Tabs
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Patient select mode
                  </Typography>
                  <Switch
                    size="small"
                    checked={settings.patientSelectMode}
                    onChange={() => setSettings(prev => ({
                      ...prev,
                      patientSelectMode: !prev.patientSelectMode,
                      ...(!prev.patientSelectMode ? { labReportPage: false, evalReportPage: false } : {})
                    }))}
                    color="primary"
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Lab results page
                  </Typography>
                  <Switch
                    size="small"
                    checked={settings.labReportPage}
                    onChange={() => setSettings(prev => ({
                      ...prev,
                      labReportPage: !prev.labReportPage,
                      ...(!prev.labReportPage ? { patientSelectMode: false, evalReportPage: false } : {})
                    }))}
                    color="primary"
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Eval report page
                  </Typography>
                  <Switch
                    size="small"
                    checked={settings.evalReportPage}
                    onChange={() => setSettings(prev => ({
                      ...prev,
                      evalReportPage: !prev.evalReportPage,
                      ...(!prev.evalReportPage ? { patientSelectMode: false, labReportPage: false } : {})
                    }))}
                    color="primary"
                  />
                </Box>
                {settings.patientSelectMode && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Visible patients ({visiblePatientList.length})
                      {visiblePatientSort?.sort_by && (
                        <Typography component="span" variant="caption" display="block" color="text.secondary">
                          Sorted by: {visiblePatientSort.sort_by}{visiblePatientSort.sort_dir === 'desc' ? ' (desc)' : ''}
                        </Typography>
                      )}
                    </Typography>
                    <Box ref={visiblePatientListScrollRef} sx={{ maxHeight: 200, overflow: 'auto' }}>
                      {visiblePatientList.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          No patients in list
                        </Typography>
                      ) : (
                        visiblePatientList.map((p) => (
                          <Box key={p.character_id ?? p.id} sx={{ py: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ flex: 1, minWidth: 0, pr: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <PatientProfileAvatar persona={p} size={28} />
                              <Typography variant="body2" sx={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {p.name ?? `Patient ${p.character_id ?? p.id}`}
                              </Typography>
                              {p.has_v2_context ? <PatientV2Badge /> : null}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              #{p.character_id ?? p.id}
                            </Typography>
                          </Box>
                        ))
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Box>
      </Box>

      {/* End Simulation Dialog */}
      <Dialog open={showEndDialog} onClose={() => setShowEndDialog(false)}>
        <DialogTitle>End Simulation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to end this simulation?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEndDialog(false)}>Cancel</Button>
          <Button onClick={confirmEndSimulation} variant="contained" color="error">
            End Simulation
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Evaluation Results Dialog */}
      <Dialog 
        open={!!evaluation} 
        onClose={() => {
          setEvaluation(null);
          navigate(SIMULATION_SETUP_PATH);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <AssessmentIcon color="primary" />
            <Typography variant="h6">Simulation Complete - Final Report</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {evaluationReport && (
            <Box sx={{ mt: 2 }}>
              {/* Medical History Section */}
              {evaluationReport.medical_history && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Medical History
                  </Typography>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      Progress
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {evaluationReport.medical_history.total - evaluationReport.medical_history.remaining}/{evaluationReport.medical_history.total}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={evaluationReport.medical_history.total > 0 
                      ? ((evaluationReport.medical_history.total - evaluationReport.medical_history.remaining) / evaluationReport.medical_history.total) * 100 
                      : 0
                    }
                    sx={{ height: 8, borderRadius: 1, mb: 1 }}
                  />
                  {evaluationReport.medical_history.executed_objects?.length > 0 && (
                    <Box sx={{ pl: 2 }}>
                      {evaluationReport.medical_history.executed_objects.map((obj, idx) => (
                        <Typography key={idx} variant="body2" color="text.secondary">
                          ✓ {obj.object}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {/* Exam Section */}
              {evaluationReport.exam && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Physical Exam
                  </Typography>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      Progress
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {evaluationReport.exam.total - evaluationReport.exam.remaining}/{evaluationReport.exam.total}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={evaluationReport.exam.total > 0 
                      ? ((evaluationReport.exam.total - evaluationReport.exam.remaining) / evaluationReport.exam.total) * 100 
                      : 0
                    }
                    sx={{ height: 8, borderRadius: 1, mb: 1 }}
                  />
                  {evaluationReport.exam.executed_objects?.length > 0 && (
                    <Box sx={{ pl: 2 }}>
                      {evaluationReport.exam.executed_objects.map((obj, idx) => (
                        <Typography key={idx} variant="body2" color="text.secondary">
                          ✓ {obj.object}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {/* Investigate Section */}
              {evaluationReport.investigate && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Investigations
                  </Typography>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      Progress
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {evaluationReport.investigate.total - evaluationReport.investigate.remaining}/{evaluationReport.investigate.total}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={evaluationReport.investigate.total > 0 
                      ? ((evaluationReport.investigate.total - evaluationReport.investigate.remaining) / evaluationReport.investigate.total) * 100 
                      : 0
                    }
                    sx={{ height: 8, borderRadius: 1, mb: 1 }}
                  />
                  {evaluationReport.investigate.executed_objects?.length > 0 && (
                    <Box sx={{ pl: 2 }}>
                      {evaluationReport.investigate.executed_objects.map((obj, idx) => (
                        <Typography key={idx} variant="body2" color="text.secondary">
                          ✓ {obj.object}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {/* Intervene Section */}
              {evaluationReport.intervene && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Interventions
                  </Typography>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      Progress
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {evaluationReport.intervene.total - evaluationReport.intervene.remaining}/{evaluationReport.intervene.total}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={evaluationReport.intervene.total > 0 
                      ? ((evaluationReport.intervene.total - evaluationReport.intervene.remaining) / evaluationReport.intervene.total) * 100 
                      : 0
                    }
                    sx={{ height: 8, borderRadius: 1, mb: 1 }}
                  />
                  {evaluationReport.intervene.executed_objects?.length > 0 && (
                    <Box sx={{ pl: 2 }}>
                      {evaluationReport.intervene.executed_objects.map((obj, idx) => (
                        <Typography key={idx} variant="body2" color="text.secondary">
                          ✓ {obj.object}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {/* Consultation Section */}
              {!hideDoctorOnlyEvalSections && evaluationReport.consultation && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Consultation
                  </Typography>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      Progress
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {evaluationReport.consultation.total - evaluationReport.consultation.remaining}/{evaluationReport.consultation.total}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={evaluationReport.consultation.total > 0 
                      ? ((evaluationReport.consultation.total - evaluationReport.consultation.remaining) / evaluationReport.consultation.total) * 100 
                      : 0
                    }
                    sx={{ height: 8, borderRadius: 1, mb: 1 }}
                  />
                  {evaluationReport.consultation.executed_objects?.length > 0 && (
                    <Box sx={{ pl: 2 }}>
                      {evaluationReport.consultation.executed_objects.map((obj, idx) => (
                        <Typography key={idx} variant="body2" color="text.secondary">
                          ✓ {obj.object}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {/* Diagnosis Section */}
              {!hideDoctorOnlyEvalSections && evaluationReport.diagnosis && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Diagnosis
                  </Typography>
                  {evaluationReport.diagnosis.latest && evaluationReport.diagnosis.latest.object ? (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Latest: {evaluationReport.diagnosis.latest.object}
                      </Typography>
                      {evaluationReport.diagnosis.latest.evaluation && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', ml: 2 }}>
                          ({evaluationReport.diagnosis.latest.evaluation})
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No diagnosis executed yet
                    </Typography>
                  )}
                  {evaluationReport.diagnosis.executed_objects?.length > 0 && (
                    <Box sx={{ pl: 2, mt: 1 }}>
                      {evaluationReport.diagnosis.executed_objects.map((obj, idx) => (
                        <Typography key={idx} variant="body2" color="text.secondary">
                          ✓ {obj.object}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {/* Handoff Section */}
              {!hideDoctorOnlyEvalSections && evaluationReport.handoff && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Handoff
                  </Typography>
                  {evaluationReport.handoff.latest && evaluationReport.handoff.latest.object ? (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Latest: {evaluationReport.handoff.latest.object}
                      </Typography>
                      {evaluationReport.handoff.latest.evaluation && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', ml: 2 }}>
                          ({evaluationReport.handoff.latest.evaluation})
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No handoff executed yet
                    </Typography>
                  )}
                  {evaluationReport.handoff.executed_objects?.length > 0 && (
                    <Box sx={{ pl: 2, mt: 1 }}>
                      {evaluationReport.handoff.executed_objects.map((obj, idx) => (
                        <Typography key={idx} variant="body2" color="text.secondary">
                          ✓ {obj.object}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setEvaluation(null);
              navigate(SIMULATION_SETUP_PATH);
            }}
            variant="contained"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Simulation;
